"use client";

import { ShoppingBag, SlidersHorizontal, MoreHorizontal } from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { useNotificationContext } from "@/contexts/NotificationContext";
import LoadingSpinner from "@/components/shared/LoadingSpinner";
import NavSheet from "./NavSheet";
// Export the height calculation function
export const getTabBarHeight = (isStandalone: boolean) => {
  if (isStandalone) {
    return `calc(100px + env(safe-area-inset-bottom))`;
  }
  return `calc(84px + env(safe-area-inset-bottom))`;
};

const TabBar = () => {
  const pathname = usePathname();
  const router = useRouter();
  const { pips } = useNotificationContext();
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(pathname);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setIsStandalone(window.matchMedia("(display-mode: standalone)").matches);
    }
  }, []);

  // Update activeTab when pathname changes
  useEffect(() => {
    setActiveTab(pathname);
    setIsLoading(false);
  }, [pathname]);

  // Function to check if a tab is active using local state
  const isActive = (path: string) => activeTab === path;

  // Handle navigation with immediate visual feedback
  const handleNavigation = async (path: string) => {
    if (path === activeTab) return; // Don't do anything if already on the page

    // Close the sheet if it's open
    setIsSheetOpen(false);

    // Immediately update the active tab
    setActiveTab(path);

    router.push(path);
  };

  // Use the exported function
  const tabBarHeight = getTabBarHeight(isStandalone);

  // Helper function to render pip
  const renderPip = (tab: keyof typeof pips) => {
    const pipValue = pips[tab];
    if (!pipValue || (typeof pipValue === "number" && pipValue === 0))
      return null;

    return (
      <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
        {typeof pipValue === "number" ? pipValue : ""}
      </div>
    );
  };

  return (
    <>
      {isLoading && (
        <div className="fixed inset-0 z-40">
          <LoadingSpinner />
        </div>
      )}
      <nav
        className="fixed bottom-0 right-0 left-0 flex justify-between items-center bg-background/95 backdrop-blur-sm border-t shadow-md z-50 px-1 sm:px-2 md:px-4"
        style={{
          paddingBottom: isStandalone
            ? "calc(env(safe-area-inset-bottom) + 16px)"
            : "env(safe-area-inset-bottom)",
          height: tabBarHeight,
        }}
      >
        {/* More Tab with Sheet */}
        <NavSheet
          isOpen={isSheetOpen}
          onOpenChange={setIsSheetOpen}
          onNavigate={handleNavigation}
          trigger={
            <button
              className="flex flex-col justify-center items-center p-1 xs:p-2 sm:p-3 md:p-4 lg:p-6 w-1/3 transition-all duration-200 text-muted hover:text-muted-foreground active:scale-90"
              aria-label="More"
            >
              <div className="relative">
                <MoreHorizontal size={28} strokeWidth={2} />
              </div>
              <span className="text-[8px] xs:text-[9px] sm:text-xs font-medium mt-1">
                More
              </span>
            </button>
          }
        />

        {/* Batches Tab - Center */}
        <button
          className={`flex flex-col justify-center items-center p-1 xs:p-2 sm:p-3 md:p-4 lg:p-6 w-1/3 transition-all duration-200 active:scale-90 ${
            isActive("/dashboard")
              ? "text-primary"
              : "text-muted hover:text-muted-foreground"
          }`}
          onClick={() => handleNavigation("/dashboard")}
          aria-label="Batches"
        >
          <div className="relative">
            <ShoppingBag size={36} strokeWidth={2} />
            {renderPip("dashboard")}
          </div>
          <span className="text-[8px] xs:text-[9px] sm:text-xs font-medium mt-1">
            Batches
          </span>
        </button>

        {/* Filters Tab - Right */}
        <button
          className={`flex flex-col justify-center items-center p-1 xs:p-2 sm:p-3 md:p-4 lg:p-6 w-1/3 transition-all duration-200 active:scale-90 ${
            isActive("/filters")
              ? "text-primary"
              : "text-muted hover:text-muted-foreground"
          }`}
          onClick={() => handleNavigation("/filters")}
          aria-label="Filters"
        >
          <div className="relative">
            <SlidersHorizontal size={28} strokeWidth={2} />
            {renderPip("quickFilters")}
          </div>
          <span className="text-[8px] xs:text-[9px] sm:text-xs font-medium mt-1">
            Filters
          </span>
        </button>
      </nav>
    </>
  );
};

export default TabBar;
